import {
  Alert,
  Button,
  Container,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useRef, useState } from "react";
import { UserContext } from "../contexts/user";
import { saveCreateForm } from "../axios/axiosFunctions";
import { useNavigate } from "react-router-dom";

/**
 * TIM-specific profile form (members only).
 * Company + team name flow from the admin invite (W17) and are not editable.
 * Senior leaders never reach this page — verifyOTP marks firstTime=false for them (W15).
 */
const TimMemberProfileForm = ({ initialUser, timContext }) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);

  const [form, setForm] = useState({
    firstName: initialUser?.firstName || currentUser.firstName || "",
    lastName: initialUser?.lastName || currentUser.lastName || "",
    email: initialUser?.email || currentUser.email || "",
    howLongCompany: initialUser?.info?.howLongCompany || "",
    tenureTeam: initialUser?.info?.tenureTeam || "",
    teamRole: initialUser?.info?.teamRole || "",
    teamRoleOther: initialUser?.info?.teamRoleOther || "",
    meetingsAttended: initialUser?.info?.meetingsAttended || "",
    meetingsVirtual: initialUser?.info?.meetingsVirtual || "",
    yearRange: initialUser?.info?.yearRange || "",
    gender: initialUser?.info?.gender || "",
  });
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [invalidFields, setInvalidFields] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const tenureCompanyRef = useRef(null);
  const tenureTeamRef = useRef(null);
  const teamRoleRef = useRef(null);
  const meetingsAttendedRef = useRef(null);
  const meetingsVirtualRef = useRef(null);

  const fieldRefs = {
    howLongCompany: tenureCompanyRef,
    tenureTeam: tenureTeamRef,
    teamRole: teamRoleRef,
    meetingsAttended: meetingsAttendedRef,
    meetingsVirtual: meetingsVirtualRef,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const validate = () => {
    const invalid = [];
    if (!form.howLongCompany) invalid.push("howLongCompany");
    if (!form.tenureTeam) invalid.push("tenureTeam");
    if (!form.teamRole) invalid.push("teamRole");
    if (!form.meetingsAttended) invalid.push("meetingsAttended");
    if (!form.meetingsVirtual) invalid.push("meetingsVirtual");
    return invalid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const invalid = validate();
    if (invalid.length > 0) {
      setInvalidFields(invalid);
      setError("Please fill the required information.");
      setShowError(true);
      const firstRef = fieldRefs[invalid[0]];
      if (firstRef && firstRef.current) {
        firstRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setTimeout(() => {
        setShowError(false);
        setInvalidFields([]);
      }, 4000);
      return;
    }

    setSubmitting(true);
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      // Company is auto-populated from the admin invite; ignore any user input.
      company: timContext?.companyName || "",
      howLongCompany: form.howLongCompany,
      tenureTeam: form.tenureTeam,
      teamRole: form.teamRole,
      teamRoleOther: form.teamRole === "other" ? form.teamRoleOther : "",
      meetingsAttended: form.meetingsAttended,
      meetingsVirtual: form.meetingsVirtual,
      yearRange: form.yearRange,
      gender: form.gender,
    };

    const res = await saveCreateForm(currentUser._id, payload);

    if (!res || !res.status) {
      setError(res?.msg || "Could not save your profile. Please try again.");
      setShowError(true);
      setSubmitting(false);
      setTimeout(() => setShowError(false), 4000);
      return;
    }

    setError(res.msg || "Saved");
    setShowError(true);
    setAlertSeverity("success");
    const updatedUser = { ...currentUser, firstTime: false };
    localStorage.setItem("userK2G", JSON.stringify(updatedUser));
    setTimeout(() => {
      navigate("/");
      setShowError(false);
    }, 1500);
  };

  const labelColor = (key) =>
    invalidFields.includes(key) ? "error.main" : "text.primary";

  return (
    <Container sx={{ marginTop: "50px" }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={6}>
            <Typography variant="h6" color="secondary">First Name *</Typography>
            <TextField required value={form.firstName} fullWidth onChange={handleChange} name="firstName" />
          </Grid>
          <Grid size={6}>
            <Typography variant="h6" color="secondary">Last Name *</Typography>
            <TextField required value={form.lastName} fullWidth onChange={handleChange} name="lastName" />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6" color="secondary">Email *</Typography>
            <TextField required value={form.email} fullWidth onChange={handleChange} name="email" />
          </Grid>

          <Grid size={6}>
            <Typography variant="h6" color="secondary">Company</Typography>
            <TextField value={timContext?.companyName || ""} fullWidth disabled />
          </Grid>
          <Grid size={6}>
            <Typography variant="h6" color="secondary">Team</Typography>
            <TextField value={timContext?.teamName || ""} fullWidth disabled />
          </Grid>

          <Grid size={12} ref={tenureCompanyRef}>
            <Typography variant="h6" sx={{ color: labelColor("howLongCompany"), transition: "color 0.3s ease" }}>
              How long have you been at the company? *
            </Typography>
            <RadioGroup row value={form.howLongCompany} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="less6" name="howLongCompany" control={<Radio />} label="Less than 6 months" />
              <FormControlLabel value="6to1" name="howLongCompany" control={<Radio />} label="6 months to 1 year" />
              <FormControlLabel value="1to3" name="howLongCompany" control={<Radio />} label="1-3 years" />
              <FormControlLabel value="more3" name="howLongCompany" control={<Radio />} label="More than 3 years" />
            </RadioGroup>
          </Grid>

          <Grid size={12} ref={tenureTeamRef}>
            <Typography variant="h6" sx={{ color: labelColor("tenureTeam"), transition: "color 0.3s ease" }}>
              How long have you been a member of this team? *
            </Typography>
            <RadioGroup row value={form.tenureTeam} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="less6" name="tenureTeam" control={<Radio />} label="Less than 6 months" />
              <FormControlLabel value="6to1" name="tenureTeam" control={<Radio />} label="6 months to 1 year" />
              <FormControlLabel value="1to3" name="tenureTeam" control={<Radio />} label="1-3 years" />
              <FormControlLabel value="more3" name="tenureTeam" control={<Radio />} label="More than 3 years" />
            </RadioGroup>
          </Grid>

          <Grid size={12} ref={teamRoleRef}>
            <Typography variant="h6" sx={{ color: labelColor("teamRole"), transition: "color 0.3s ease" }}>
              What is your role on the team? *
            </Typography>
            <RadioGroup row value={form.teamRole} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="leader" name="teamRole" control={<Radio />} label="Team Leader" />
              <FormControlLabel value="member" name="teamRole" control={<Radio />} label="Team Member" />
              <FormControlLabel value="sponsor" name="teamRole" control={<Radio />} label="Team Executive Sponsor" />
              <FormControlLabel value="other" name="teamRole" control={<Radio />} label="Other" />
            </RadioGroup>
            {form.teamRole === "other" && (
              <TextField
                placeholder="Describe your role"
                value={form.teamRoleOther}
                onChange={handleChange}
                name="teamRoleOther"
                fullWidth
                sx={{ marginTop: "10px" }}
              />
            )}
          </Grid>

          <Grid size={12} ref={meetingsAttendedRef}>
            <Typography variant="h6" sx={{ color: labelColor("meetingsAttended"), transition: "color 0.3s ease" }}>
              What percent of the team meetings do you attend? *
            </Typography>
            <RadioGroup row value={form.meetingsAttended} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="lt50" name="meetingsAttended" control={<Radio />} label="Less than 50%" />
              <FormControlLabel value="50to80" name="meetingsAttended" control={<Radio />} label="50-80%" />
              <FormControlLabel value="gt80" name="meetingsAttended" control={<Radio />} label="Greater than 80%" />
            </RadioGroup>
          </Grid>

          <Grid size={12} ref={meetingsVirtualRef}>
            <Typography variant="h6" sx={{ color: labelColor("meetingsVirtual"), transition: "color 0.3s ease" }}>
              What percent of the team meetings do you attend virtually? *
            </Typography>
            <RadioGroup row value={form.meetingsVirtual} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="veryFew" name="meetingsVirtual" control={<Radio />} label="Very Few" />
              <FormControlLabel value="some" name="meetingsVirtual" control={<Radio />} label="Some" />
              <FormControlLabel value="most" name="meetingsVirtual" control={<Radio />} label="Most" />
              <FormControlLabel value="all" name="meetingsVirtual" control={<Radio />} label="All" />
            </RadioGroup>
          </Grid>

          <Grid size={12}>
            <Typography variant="h6" color="secondary">Optional questions used for research purposes only:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Please indicate your birth year range</Typography>
            <RadioGroup row value={form.yearRange} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="later1996" name="yearRange" control={<Radio />} label="1996 or later" />
              <FormControlLabel value="1977to1995" name="yearRange" control={<Radio />} label="1977-1995" />
              <FormControlLabel value="1965to1976" name="yearRange" control={<Radio />} label="1965-1976" />
              <FormControlLabel value="1946to1964" name="yearRange" control={<Radio />} label="1946-1964" />
              <FormControlLabel value="before1945" name="yearRange" control={<Radio />} label="1945 or before" />
            </RadioGroup>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Please indicate your gender</Typography>
            <RadioGroup row value={form.gender} onChange={handleChange} sx={{ justifyContent: "space-between", padding: "0px 10px" }}>
              <FormControlLabel value="female" name="gender" control={<Radio />} label="Female" />
              <FormControlLabel value="male" name="gender" control={<Radio />} label="Male" />
              <FormControlLabel value="trans" name="gender" control={<Radio />} label="Transgender" />
              <FormControlLabel value="nonBinary" name="gender" control={<Radio />} label="Non-binary" />
              <FormControlLabel value="notListed" name="gender" control={<Radio />} label="Not Listed" />
              <FormControlLabel value="notAnswer" name="gender" control={<Radio />} label="Prefer not to answer" />
            </RadioGroup>
          </Grid>

          {showError && (
            <Grid size={12}>
              <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>{error}</Alert>
            </Grid>
          )}
          <Grid size={12}>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>* Required field</Typography>
          </Grid>
          <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Button type="submit" disabled={submitting} sx={{ marginTop: { xs: "20px", sm: "50px" }, marginBottom: "10px" }} size="large" variant="contained" color="secondary">
              {submitting ? "SAVING..." : "SAVE AND CONTINUE"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default TimMemberProfileForm;
